import { useState } from 'react';
import { useWebhooks, useCreateWebhook, useDeleteWebhook, useTestWebhook } from '../api/intelligence';
import type { WebhookConfig } from '../api/intelligence';

const EVENT_TYPES = [
  { key: 'alert_created', label: 'Alerte creee' },
  { key: 'anomaly_detected', label: 'Anomalie detectee' },
  { key: 'workflow_transition', label: 'Transition workflow' },
  { key: 'entity_created', label: 'Entite creee' },
  { key: 'entity_merged', label: 'Entite fusionnee' },
];

export default function WebhooksPage() {
  const { data: webhooks, isLoading, refetch } = useWebhooks();
  const createMutation = useCreateWebhook();
  const deleteMutation = useDeleteWebhook();
  const testMutation = useTestWebhook();

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [events, setEvents] = useState<string[]>([]);

  const toggleEvent = (key: string) => {
    setEvents((prev) => (prev.includes(key) ? prev.filter((e) => e !== key) : [...prev, key]));
  };

  const handleCreate = async () => {
    if (!name || !url || events.length === 0) return;
    await createMutation.mutateAsync({ name, url, events, secret: secret || undefined });
    setShowCreate(false);
    setName('');
    setUrl('');
    setSecret('');
    setEvents([]);
    refetch();
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
    refetch();
  };

  const handleTest = (id: string) => testMutation.mutate(id);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Webhooks</h1>
          <p className="text-sm text-gray-500 mt-1">Notifications sortantes vers systemes externes</p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 text-sm font-medium bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors"
        >
          + Nouveau webhook
        </button>
      </div>

      {showCreate && (
        <div className="glass-panel p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nom du webhook..."
              className="px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300"
            />
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://hooks.slack.com/..."
              className="px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300"
            />
          </div>
          <input
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="Secret HMAC (optionnel)"
            className="w-full px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300"
          />
          <div>
            <p className="text-xs text-gray-500 mb-2">Evenements</p>
            <div className="flex flex-wrap gap-2">
              {EVENT_TYPES.map((evt) => (
                <button
                  key={evt.key}
                  onClick={() => toggleEvent(evt.key)}
                  className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                    events.includes(evt.key)
                      ? 'bg-cyber-cyan/10 border-cyber-cyan/30 text-cyber-cyan'
                      : 'bg-surface-tertiary border-border-subtle text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {evt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-400">Annuler</button>
            <button
              onClick={handleCreate}
              disabled={!name || !url || events.length === 0 || createMutation.isPending}
              className="px-4 py-2 text-sm font-medium bg-cyber-cyan text-surface-primary rounded-lg hover:bg-cyan-400 disabled:opacity-50"
            >
              Creer
            </button>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
        </div>
      )}

      {webhooks && webhooks.length > 0 && (
        <div className="space-y-3">
          {webhooks.map((wh: WebhookConfig) => (
            <div key={wh.id} className="glass-panel p-4 flex items-center gap-4">
              <div className={`w-3 h-3 rounded-full ${wh.active ? 'bg-green-400' : 'bg-gray-500'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-200">{wh.name}</p>
                <p className="text-xs text-gray-500 font-mono truncate">{wh.url}</p>
                <div className="flex gap-1 mt-1">
                  {wh.events.map((evt) => (
                    <span key={evt} className="px-1.5 py-0.5 text-[10px] bg-surface-tertiary text-gray-400 rounded">{evt}</span>
                  ))}
                </div>
              </div>
              <div className="text-xs text-gray-600">
                {wh.last_triggered_at ? new Date(wh.last_triggered_at).toLocaleDateString('fr-FR') : 'Jamais'}
              </div>
              <button
                onClick={() => handleTest(wh.id)}
                disabled={testMutation.isPending}
                className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-400 rounded-lg hover:text-gray-200 transition-colors"
              >
                Tester
              </button>
              <button
                onClick={() => handleDelete(wh.id)}
                disabled={deleteMutation.isPending}
                className="px-3 py-1.5 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
              >
                Supprimer
              </button>
            </div>
          ))}
        </div>
      )}

      {!isLoading && webhooks && webhooks.length === 0 && (
        <p className="text-center text-gray-500 py-12">Aucun webhook configure</p>
      )}
    </div>
  );
}
